import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Activity, Brain, Store, Loader2, Plus, Trash2, ExternalLink, Copy, Check,
  Inbox, Wallet, ShieldAlert, Globe, Stethoscope, ClipboardList,
  QrCode, Download, Share2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_PRESETS = ['#0F172A', '#0F4C5C', '#5B3A29', '#3D2C5F', '#7A2E2E', '#1F4E79'];
const LEAD_STATUS = { new: 'bg-amber-50 text-amber-700 border-amber-200', contacted: 'bg-blue-50 text-blue-700 border-blue-200', converted: 'bg-emerald-50 text-emerald-700 border-emerald-200', archived: 'bg-cn-soft text-cn-text-muted border-cn-border' };

export default function ClinicianPractice() {
  const { getAuthHeader, token } = useAuth();
  const [tab, setTab] = useState('page');
  const [agency, setAgency] = useState(null);
  const [leads, setLeads] = useState([]);
  const [newLeads, setNewLeads] = useState(0);
  const [billing, setBilling] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [blurbCopied, setBlurbCopied] = useState(false);
  const qrRef = useRef(null);

  const loadAgency = useCallback(async () => {
    const { data } = await axios.get(`${API}/clinician/micro-agency`, getAuthHeader());
    setAgency(data);
  }, [getAuthHeader]);
  const loadLeads = useCallback(async () => {
    const { data } = await axios.get(`${API}/clinician/micro-agency/leads`, getAuthHeader());
    setLeads(data.leads); setNewLeads(data.new_count);
  }, [getAuthHeader]);
  const loadBilling = useCallback(async () => {
    const { data } = await axios.get(`${API}/clinician/micro-agency/billing`, getAuthHeader());
    setBilling(data);
  }, [getAuthHeader]);

  useEffect(() => { if (token) { loadAgency(); loadLeads(); loadBilling(); } }, [token, loadAgency, loadLeads, loadBilling]);

  const upd = (patch) => setAgency((a) => ({ ...a, ...patch }));
  const updService = (i, patch) => setAgency((a) => ({ ...a, services: a.services.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const addService = () => setAgency((a) => ({ ...a, services: [...(a.services || []), { name: '', rate: '', unit: 'visit' }] }));
  const removeService = (i) => setAgency((a) => ({ ...a, services: a.services.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: agency.business_name, slug: agency.slug, tagline: agency.tagline,
        about: agency.about, brand_color: agency.brand_color, accepting_clients: agency.accepting_clients,
        published: agency.published,
        services: (agency.services || []).filter((s) => s.name).map((s) => ({ name: s.name, rate: s.rate === '' ? null : Number(s.rate), unit: s.unit || 'visit' })),
      };
      const { data } = await axios.put(`${API}/clinician/micro-agency`, payload, getAuthHeader());
      setAgency(data);
      toast.success('Practice page saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const setLeadStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
    try { await axios.patch(`${API}/clinician/micro-agency/leads/${id}`, { status }, getAuthHeader()); loadLeads(); }
    catch (e) { toast.error('Could not update'); loadLeads(); }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(agency.public_url);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed — link is shown above'); }
  };

  const shareUrl = agency ? `${agency.public_url}${(agency.public_url || '').includes('?') ? '&' : '?'}ref=qr` : '';
  const shareBlurb = agency
    ? `Looking for trusted home care? I'm ${agency.business_name || 'an independent clinician'} on CaraLoom — verified, clinical-grade care you can book directly. Request a visit here: ${shareUrl}`
    : '';
  const copyBlurb = async () => {
    try {
      await navigator.clipboard.writeText(shareBlurb);
      setBlurbCopied(true); toast.success('Share blurb copied'); setTimeout(() => setBlurbCopied(false), 1500);
    } catch { toast.error('Copy failed — the blurb is shown below'); }
  };
  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${agency.slug || 'caraloom-practice'}-qr.png`;
    a.click();
    toast.success('QR code downloaded');
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
    { icon: Brain, label: 'Risk Monitor', href: '/clinician/risk-monitor' },
    { icon: Store, label: 'My Practice', active: true, highlight: true },
    { icon: ClipboardList, label: 'Clinical Notes', href: '/clinician/notes' },
    { icon: Stethoscope, label: 'Care Requests', href: '/clinician/care-requests' },
  ];

  if (!agency) return <DashboardShell navItems={navItems} title="My Practice" testId="practice-page"><div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div></DashboardShell>;

  return (
    <DashboardShell
      navItems={navItems}
      title="My Practice — Business in a Box"
      subtitle="Your own white-labeled care page, client intake, and automated billing — powered by CaraLoom."
      testId="practice-page"
    >
      {/* Tabs */}
      <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-cn-soft/50 border border-cn-border mb-6" data-testid="practice-tabs">
        {[{ k: 'page', label: 'My Page' }, { k: 'leads', label: `Leads${newLeads ? ` (${newLeads})` : ''}` }, { k: 'billing', label: 'Billing' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all ${tab === t.k ? 'bg-white dark:bg-cn-ink-800 shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'page' && (
        <div className="grid lg:grid-cols-5 gap-6" data-testid="practice-page-settings">
          <div className="lg:col-span-3 space-y-4">
            <div className="cn-card rounded-2xl p-5 space-y-4">
              <div>
                <Label className="text-xs">Business name</Label>
                <Input value={agency.business_name || ''} onChange={(e) => upd({ business_name: e.target.value })} data-testid="practice-business-name" />
              </div>
              <div>
                <Label className="text-xs">Your CaraLoom URL handle</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-cn-text-muted">caraloom.com/c/</span>
                  <Input value={agency.slug || ''} onChange={(e) => upd({ slug: e.target.value })} className="flex-1" data-testid="practice-slug" />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <a href={agency.public_url} target="_blank" rel="noreferrer" className="text-xs text-cn-primary inline-flex items-center gap-1" data-testid="practice-view-public">
                    <Globe className="w-3.5 h-3.5" /> View public page <ExternalLink className="w-3 h-3" />
                  </a>
                  <button onClick={copyUrl} className="text-xs text-cn-text-muted inline-flex items-center gap-1" data-testid="practice-copy-url">
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy link'}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Tagline</Label>
                <Input value={agency.tagline || ''} onChange={(e) => upd({ tagline: e.target.value })} data-testid="practice-tagline" />
              </div>
              <div>
                <Label className="text-xs">About your practice</Label>
                <Textarea value={agency.about || ''} onChange={(e) => upd({ about: e.target.value })} rows={4} data-testid="practice-about" />
              </div>
              <div>
                <Label className="text-xs">Brand color</Label>
                <div className="flex items-center gap-2 mt-1">
                  {BRAND_PRESETS.map((c) => (
                    <button key={c} onClick={() => upd({ brand_color: c })} className={`w-8 h-8 rounded-full border-2 ${agency.brand_color === c ? 'border-cn-text' : 'border-transparent'}`} style={{ background: c }} data-testid={`brand-${c.slice(1)}`} />
                  ))}
                  <input type="color" value={agency.brand_color || '#0F172A'} onChange={(e) => upd({ brand_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer" data-testid="practice-brand-color" />
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="cn-card rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-cn-text">Services & rates</p>
                <button onClick={addService} className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-sm" data-testid="add-service"><Plus className="w-4 h-4" /> Add</button>
              </div>
              <div className="space-y-2" data-testid="services-editor">
                {(agency.services || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`service-row-${i}`}>
                    <Input value={s.name} onChange={(e) => updService(i, { name: e.target.value })} placeholder="Service name" className="flex-1" data-testid={`service-name-${i}`} />
                    <Input type="number" value={s.rate ?? ''} onChange={(e) => updService(i, { rate: e.target.value })} placeholder="$" className="w-24" data-testid={`service-rate-${i}`} />
                    <button onClick={() => removeService(i)} className="text-cn-text-muted hover:text-red-500 p-2" data-testid={`service-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {(agency.services || []).length === 0 && <p className="text-xs text-cn-text-muted">No services yet — add the care you offer.</p>}
              </div>
            </div>
          </div>

          {/* Publish controls */}
          <div className="lg:col-span-2 space-y-4">
            <div className="cn-card rounded-2xl p-5 space-y-3">
              <button onClick={() => upd({ accepting_clients: !agency.accepting_clients })} className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl border text-sm ${agency.accepting_clients ? 'border-cn-primary bg-cn-soft' : 'border-cn-border text-cn-text-muted'}`} data-testid="toggle-accepting">
                <span>Accepting new clients</span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 ${agency.accepting_clients ? 'bg-cn-primary justify-end' : 'bg-cn-border justify-start'}`}><span className="w-4 h-4 rounded-full bg-white" /></span>
              </button>
              <button onClick={() => upd({ published: !agency.published })} className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl border text-sm ${agency.published ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-cn-border text-cn-text-muted'}`} data-testid="toggle-published">
                <span>{agency.published ? 'Page is live' : 'Page is hidden'}</span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 ${agency.published ? 'bg-emerald-500 justify-end' : 'bg-cn-border justify-start'}`}><span className="w-4 h-4 rounded-full bg-white" /></span>
              </button>
              <button onClick={save} disabled={saving} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="practice-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save changes
              </button>
            </div>
            <div className="cn-card rounded-2xl p-5">
              <p className="text-xs text-cn-text-muted">Share your link on social, in discharge paperwork, or with families. Every request lands in your <button onClick={() => setTab('leads')} className="text-cn-primary font-semibold">Leads</button> inbox.</p>
            </div>

            {/* Share my practice kit */}
            <div className="cn-card rounded-2xl p-5" data-testid="share-kit-card">
              <div className="flex items-center gap-2 mb-1">
                <Share2 className="w-4 h-4 text-cn-primary" />
                <p className="text-sm font-bold text-cn-text">Share my practice</p>
              </div>
              <p className="text-xs text-cn-text-muted mb-4">One-tap kit to turn every conversation into a referral. {!agency.published && <span className="text-amber-600 font-medium">Publish your page so the link goes live.</span>}</p>

              {/* QR code */}
              <div className="flex flex-col items-center">
                <div ref={qrRef} className="rounded-2xl bg-white border border-cn-border p-3" data-testid="practice-qr">
                  <QRCodeCanvas
                    value={shareUrl || 'https://caraloom.com'}
                    size={150}
                    level="M"
                    fgColor="#0F172A"
                    bgColor="#FFFFFF"
                    includeMargin={false}
                  />
                </div>
                <p className="mt-2 text-[11px] text-cn-text-muted flex items-center gap-1"><QrCode className="w-3.5 h-3.5" /> Scan to open your public page</p>
                <button onClick={downloadQr} className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs mt-2" data-testid="download-qr-btn">
                  <Download className="w-3.5 h-3.5" /> Download QR
                </button>
              </div>

              {/* Copy blurb */}
              <div className="mt-4">
                <Label className="text-xs">Ready-to-send blurb</Label>
                <Textarea value={shareBlurb} readOnly rows={4} className="text-xs mt-1 resize-none" data-testid="share-blurb-text" />
                <button onClick={copyBlurb} className="cn-btn cn-btn-primary w-full justify-center !py-2.5 mt-2" data-testid="copy-blurb-btn">
                  {blurbCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {blurbCopied ? 'Copied' : 'Copy blurb'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'leads' && (
        <div data-testid="practice-leads">
          {leads.length === 0 ? (
            <div className="cn-card rounded-2xl p-12 text-center" data-testid="leads-empty">
              <Inbox className="w-12 h-12 text-cn-primary mx-auto mb-3" />
              <p className="text-cn-text font-medium">No leads yet</p>
              <p className="text-sm text-cn-text-muted mt-1">Publish and share your page to start receiving care requests.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} className="cn-card rounded-2xl p-5" data-testid={`lead-${l.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-cn-text">{l.name}</p>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${LEAD_STATUS[l.status] || LEAD_STATUS.new}`}>{l.status}</span>
                      </div>
                      <p className="text-sm text-cn-text-muted mt-1">{l.care_need || 'Home care'}</p>
                      {l.message && <p className="text-sm text-cn-text mt-1 italic">"{l.message}"</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-cn-text-muted">
                        {l.contact_phone && <span className="inline-flex items-center gap-1">📞 {l.contact_phone}</span>}
                        {l.contact_email && <span className="inline-flex items-center gap-1">✉️ {l.contact_email}</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {['contacted', 'converted', 'archived'].map((s) => (
                        <button key={s} onClick={() => setLeadStatus(l.id, s)} className="text-xs px-2.5 py-1 rounded-full border border-cn-border hover:border-cn-primary text-cn-text" data-testid={`lead-${l.id}-${s}`}>
                          {s === 'converted' ? 'Won' : s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'billing' && billing && (
        <div data-testid="practice-billing">
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="cn-card rounded-2xl p-5" data-testid="billing-released">
              <Wallet className="w-5 h-5 text-cn-primary mb-2" />
              <p className="text-2xl font-display font-extrabold text-cn-text">${billing.released_earnings.toLocaleString()}</p>
              <p className="text-xs text-cn-text-muted">Released to you</p>
            </div>
            <div className="cn-card rounded-2xl p-5">
              <p className="text-2xl font-display font-extrabold text-cn-text">${billing.in_escrow.toLocaleString()}</p>
              <p className="text-xs text-cn-text-muted">Awaiting service verification</p>
            </div>
            <div className="cn-card rounded-2xl p-5">
              <p className="text-2xl font-display font-extrabold text-cn-text">{billing.lifetime_visits}</p>
              <p className="text-xs text-cn-text-muted">Lifetime visits</p>
            </div>
          </div>
          {billing.edi_simulated && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4" data-testid="edi-note">
              <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" /> {billing.edi_note}
            </div>
          )}
          <div className="cn-card rounded-2xl p-5">
            <p className="text-sm font-bold text-cn-text mb-3">EDI 837P insurance claims</p>
            {billing.claims.length === 0 ? (
              <p className="text-sm text-cn-text-muted">No claims yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-cn-text-muted text-left"><tr>
                    <th className="py-1 pr-3 font-medium">Claim</th><th className="py-1 pr-3 font-medium">Service</th>
                    <th className="py-1 pr-3 font-medium">Billed</th><th className="py-1 pr-3 font-medium">Status</th>
                  </tr></thead>
                  <tbody className="text-cn-text">
                    {billing.claims.map((c) => (
                      <tr key={c.claim_id} className="border-t border-cn-border/60" data-testid={`claim-${c.claim_id}`}>
                        <td className="py-1.5 pr-3 font-mono">{c.claim_id}</td>
                        <td className="py-1.5 pr-3">{c.service}</td>
                        <td className="py-1.5 pr-3">${c.billed}</td>
                        <td className="py-1.5 pr-3">{c.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
